import theme from "@/styles/theme";
import { ColumnForm, ContainerData } from "./styles";
import { Page } from "../page-base";
import { Input } from "../inputs";
import { DatagridResponsive } from "../datagrid-base";
import { useEffect, useState } from "react";
import { AccountsTokenName } from "@/modules/accounts/components/account-token-name";
import { toast } from "react-toastify";
import { UserService } from "@/modules/users/services";
import { AccountsService } from "@/modules/accounts/services";
import { TDatagridData, TDGKey } from "../datagrid-base/types";
import { useNavigationContext } from "@/contexts";
import { TAccountsToken } from "@/modules/accounts/models";
import permissionsStore from "../../store/permissions";
import {
  SectionDescription,
  SectionTitle,
} from "@/modules/configurations/pages/styles";

interface ITokensProps {
  entity: "users" | "accounts";
  accountId?: string;
}

export const TokensDisplay: React.FC<ITokensProps> = ({
  entity,
  accountId,
}) => {
  const [generatedToken, setGeneratedToken] = useState<string>("");
  const [canShow, setCanShow] = useState<boolean>(false);
  const [modalTokenName, setModalTokenName] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [accountsTokens, setAccountsTokens] = useState<TAccountsToken[]>([]);
  const [count, setCount] = useState<number>(0);
  const [page, setPage] = useState<number>(1);

  const { rowsPerPage } = useNavigationContext();
  const { role } = permissionsStore();

  useEffect(() => {
    fetchData();
    /* eslint-disable-next-line */
  }, [page, rowsPerPage]);

  const toggleModalTokenName = () => setModalTokenName((prev) => !prev);

  const navigateTable = (newPage: string) => {
    setPage(Number(newPage));
  };
  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedToken).then(() => {
      toast.success("Token copiado para a área de transferência!");
    });
  };

  const fetchData = async (): Promise<void> => {
    setLoading(true);
    const serviceCall =
      entity === "users"
        ? () => UserService.getSystemTokens(page, rowsPerPage, "asc")
        : () =>
            AccountsService.getAccountsTokens(
              page,
              rowsPerPage,
              Number(accountId),
              "asc"
            );

    try {
      const response = await serviceCall();
      if (response.success) {
        const total = response.meta!.total;
        setCount(total);
        if (total > 0 && response.data!.length === 0) {
          navigateTable((page - 1).toString());
        }
        setAccountsTokens(response.data!);
      } else {
        toast.error(response.message);
        setAccountsTokens([]);
        setCount(0);
      }
    } catch (err: unknown) {
      console.error("Erro ao buscar tokens:", err);
      setAccountsTokens([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  };

  const generateToken = async (tokenName: string): Promise<void> => {
    setCanShow(false);

    try {
      // Chama o serviço correto conforme a entidade
      const tokenResult =
        entity === "users"
          ? await UserService.regenerateToken(tokenName)
          : await AccountsService.regenerateToken(accountId!, tokenName);

      const { success, message, value } = tokenResult;

      if (success) {
        await fetchData();
        toast.success(message);
        setGeneratedToken(value!);
        setCanShow(true);
      } else {
        toast.error(message || "Erro ao gerar token");
      }
    } catch (error) {
      console.error("Erro ao gerar token:", error);
      toast.error("Erro ao gerar token");
    }
  };

  const deleteToken = async (apiRow: TDGKey): Promise<void> => {
    // Função que escolhe o endpoint conforme a entidade
    const callDelete = () =>
      entity === "users"
        ? UserService.deleteSystemToken(apiRow.id)
        : AccountsService.deleteAccountToken(Number(accountId), apiRow.id);

    try {
      const { success, message } = await callDelete();

      if (!success) {
        toast.error(message);
      }
      await fetchData();
      toast.success(message);
    } catch (err: unknown) {
      console.error("Erro ao deletar API key:", err);
      const errorMsg =
        err instanceof Error ? err.message : "Erro inesperado ao deletar chave";
      toast.error(errorMsg);
    }
  };
  const createRow = (tokens: TAccountsToken[]): TDatagridData[] => {
    const offset = (page - 1) * rowsPerPage;
    return tokens.map((record, index) => ({
      text: offset + index + 1,
      text2: record.name ?? "Token inicial",
      text4: record.token_preview ?? "-",
      text5: record.created_at ?? "-",
      id: record.id,
    }));
  };
  
  return (
    <>
      <AccountsTokenName
        modal={modalTokenName}
        toggleModal={toggleModalTokenName}
        generateToken={generateToken}
      />
      <ContainerData>
        <SectionTitle>Tokens</SectionTitle>
        <SectionDescription>
          Atenção! quando o token é gerado, certifique-se de armazená-lo, pois ele não
          poderá ser recuperado!
        </SectionDescription>
        <ColumnForm>
          <div className="btn">
            <Page.Button
              borderColor={theme.colors.palete.gold}
              disabled={false}
              label={!canShow ? "Gerar token" : "Gerar novo token"}
              onClick={toggleModalTokenName}
            />
          </div>
          <div className="input">
            <Input.Label
              label="Token:"
              setValue={setGeneratedToken}
              placeholder=""
              value={generatedToken}
              required
              readonly
            />
          </div>
          <div className="btn">
            <Page.Button
              label="Copiar token"
              onClick={copyToClipboard}
              disabled={!generatedToken}
            />
          </div>
        </ColumnForm>
      </ContainerData>
      <div style={{ marginTop: "2vh", placeItems: "center", width: "100%" }}>
        <DatagridResponsive.Combo
          headers={[
            { field: "ID" },
            { field: "Nome" },
            { field: "Prévia da chave" },
            { field: "Criado em" },
            ...(role !== "collaborator"
              ? [{ field: "Ações", width: "10vw" }]
              : []),
          ]}
          data={createRow(accountsTokens)}
          count={count}
          handleDelete={{
            onDelete: deleteToken,
            modalTitle: "Excluir chave",
            modalText:
              "Após a exclusão a chave não poderá mais ser recuperada.",
          }}
          navigateTable={navigateTable}
          rowHeader="text2"
          loading={loading}
          page={page}
        />
      </div>
    </>
  );
};
